// The rolling 6-month Capability chart, from Ronan's Capability mode design
// (2026-09-28). Same shape as the Learning Tier TierTrend: one point per week,
// a smooth line, this week's value called out at the end. Programs launched
// in the window (Assessments and Trainings) sit on the line as dots, so a jump
// reads against what caused it. Hover a dot for the program, the line for the week.

import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { TrendEvent, TrendSeries, TrendWindow } from './capabilityTrendData';
import { elliottTokens as t } from './add-program-chat/tokens';
import DosenTooltip from '../../../../../../components/ui/DosenTooltip';

// Validated for the dark surface (dataviz validator: lightness, chroma, CVD, contrast all pass).
export const TREND_COLORS: Record<TrendSeries['key'], string> = { capability: '#D9761A', activity: '#3D96E0' };
// Program dots: one colour per kind, both distinct from the orange line.
export const EVENT_COLORS: Record<TrendEvent['kind'], string> = { assessment: '#3D96E0', training: '#A78BFA' };
const EVENT_LABELS: Record<TrendEvent['kind'], string> = { assessment: 'Assessment', training: 'Training' };
const GRID = '#2A2A2A';

const fmtDate = (d: Date) =>
  d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

// The program tooltip matches the Team Capability % tooltip: same type, colours and row rhythm.
const TIP_TEXT = { fontFamily: 'Inter, sans-serif', fontSize: 11.5, lineHeight: 1.5, color: '#B6B6B6' };
const TIP_HEADING = { ...TIP_TEXT, color: '#FDFDFD', fontWeight: 700 };

/** Label left, value right, as in the Breakdown rows. */
const TipRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '16px', py: '2px' }}>
    <Typography sx={TIP_TEXT}>{label}</Typography>
    <Typography sx={{ ...TIP_TEXT, color: '#FDFDFD', fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

/** What a program dot opens on hover. */
const EventTooltip: React.FC<{ event: TrendEvent }> = ({ event }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <Typography sx={TIP_HEADING}>{event.title}</Typography>
    <Box>
      <TipRow
        label="Type"
        value={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Box
              component="span"
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: EVENT_COLORS[event.kind] }}
            />
            {EVENT_LABELS[event.kind]}
          </Box>
        }
      />
      <TipRow label="Launched" value={fmtDate(event.launchedOn)} />
      <TipRow label="Completion Rate" value={`${event.completionRate}%`} />
    </Box>
    <Typography sx={{ ...TIP_HEADING, mt: '6px' }}>
      {event.kind === 'assessment' ? 'Capabilities Assessed' : 'Capabilities Covered'}
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: '18px' }}>
      {event.capabilities.map((c) => (
        <Typography component="li" key={c} sx={{ ...TIP_TEXT, py: '1px' }}>
          {c}
        </Typography>
      ))}
    </Box>
  </Box>
);
const SURFACE = '#1C1C1C';

/** Catmull-Rom through the points, as a cubic Bezier path. */
const smooth = (pts: [number, number][]) => {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C ${c1[0]} ${c1[1]}, ${c2[0]} ${c2[1]}, ${p2[0]} ${p2[1]}`;
  }
  return d;
};

const fmtWeek = (d: Date) =>
  `Week of ${d.toLocaleString('en-US', { day: 'numeric', month: 'short', timeZone: 'UTC' })}`;

const CapabilityTrend: React.FC<{
  window: TrendWindow;
  series: TrendSeries[];
  /** Narrow variant for side panels. */
  compact?: boolean;
  title?: string;
  /** A line to the right of the title: why the chart is empty for now. */
  note?: string;
  /** Programs launched in the window, marked as dots on the first series' line. */
  events?: TrendEvent[];
  /** Replace the 0 / 50 / 100% axis: grid lines at these values, labels at these heights. */
  yAxis?: { lines: number[]; labels: { at: number; text: string }[] };
  /** How a value reads at the end of the line and on hover. Default: "58%". */
  formatValue?: (value: number) => string;
  /** What a point says on hover, where that is more than the label at the end of the line. */
  formatHover?: (value: number) => string;
}> = ({
  window: win,
  series,
  compact = false,
  title,
  note,
  events = [],
  yAxis,
  formatValue = (v) => `${v}%`,
  formatHover = formatValue
}) => {
  const W = compact ? 420 : 900;
  const H = compact ? 150 : 190;
  // Word labels (tier names) need more room than "100%".
  // "Exceptional" is the longest: it needs the full 82.
  const padL = yAxis ? 82 : compact ? 38 : 40;
  const monthLabels = win.months.slice(-6).map((m) => m.label);
  // Room for the value at the end of the line: a tier name is wider than "58%".
  const padR = yAxis ? 72 : compact ? 44 : 56;
  const padT = 14;
  const padB = 22;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = win.weeks.length;
  const xAt = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * plotW);
  const yAt = (v: number) => padT + (1 - v / 100) * plotH;
  const [hover, setHover] = useState<number | null>(null);
  const [hoverEvent, setHoverEvent] = useState<TrendEvent | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const drawn = useMemo(
    () =>
      series.map((s) => {
        const pts = s.points
          .map((v, i) => (v === null ? null : ([xAt(i), yAt(v)] as [number, number])))
          .filter(Boolean) as [number, number][];
        const line = smooth(pts);
        const area =
          pts.length >= 2 ? `${line} L ${pts[pts.length - 1][0]} ${padT + plotH} L ${pts[0][0]} ${padT + plotH} Z` : '';
        const lastIdx = s.points.length - 1 - [...s.points].reverse().findIndex((v) => v !== null);
        return { s, pts, line, area, lastIdx, last: s.points[lastIdx] };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, W, H]
  );

  // End labels never collide: when two sit within a line of each other, push them apart.
  const labelY: Record<string, number> = {};
  {
    const ends = drawn
      .filter((d) => d.last !== null && d.last !== undefined)
      .map((d) => ({ key: d.s.key, y: yAt(d.last as number) + 4 }));
    ends.sort((a, b) => a.y - b.y);
    for (let i = 1; i < ends.length; i++) if (ends[i].y - ends[i - 1].y < 13) ends[i].y = ends[i - 1].y + 13;
    ends.forEach((e) => {
      labelY[e.key] = e.y;
    });
  }

  // Each program sits on the line at the week it launched. Before the line
  // starts (nothing assessed yet) there is nothing to sit on, so it is left off.
  const [line] = series;
  const eventDots = events
    .map((event) => {
      const v = line?.points[event.index];
      return v === null || v === undefined ? null : { event, cx: xAt(event.index), cy: yAt(v) };
    })
    .filter((d): d is { event: TrendEvent; cx: number; cy: number } => d !== null);

  const onMove = (e: React.MouseEvent) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = ((e.clientX - r.left) / r.width) * W;
    const i = Math.round(((x - padL) / plotW) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };
  const fontAxis = compact ? 11 : 10;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {/* Title and legend: identity never rests on colour alone */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        {title && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, flex: 1 }}>{title}</Typography>
        )}
        {note && <Typography sx={{ fontSize: 11, color: t.textTertiary, textAlign: 'right' }}>{note}</Typography>}
        {/* A single series needs no legend: the title names it */}
        {series.length > 1 &&
          series.map((s) => (
            <Box key={s.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Box sx={{ width: 14, height: 2, borderRadius: 1, bgcolor: TREND_COLORS[s.key] }} />
              <Typography sx={{ fontSize: 11, color: t.textSecondary }}>{s.label}</Typography>
            </Box>
          ))}
        {/* The program dot key, top right. */}{' '}
        {(['assessment', 'training'] as const)
          .filter((kind) => events.some((e) => e.kind === kind))
          .map((kind) => (
            <Box key={kind} sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {' '}
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: EVENT_COLORS[kind] }} />{' '}
              <Typography sx={{ fontSize: 11, color: t.textSecondary }}>{EVENT_LABELS[kind]}s</Typography>{' '}
            </Box>
          ))}
      </Box>
      <Box sx={{ position: 'relative' }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          style={{ display: 'block' }}
          role="img"
          aria-label={`${series.map((s) => s.label).join(' and ')} over the last 6 months`}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            {series.map((s) => (
              <linearGradient key={s.key} id={`trend-fill-${s.key}-${compact ? 'c' : 'f'}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={TREND_COLORS[s.key]} stopOpacity={0.18} />
                <stop offset="100%" stopColor={TREND_COLORS[s.key]} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          {/* Recessive grid: 0, 50 and 100% by default, or the lines the host names */}
          {(yAxis?.lines ?? [0, 50, 100]).map((v) => (
            <line key={v} x1={padL} x2={padL + plotW} y1={yAt(v)} y2={yAt(v)} stroke={GRID} strokeWidth={1} />
          ))}
          {(yAxis?.labels ?? [0, 50, 100].map((v) => ({ at: v, text: `${v}%` }))).map((l) => (
            <text
              key={l.text}
              x={padL - 6}
              y={yAt(l.at) + 3}
              textAnchor="end"
              fontSize={fontAxis}
              fill={t.textTertiary}
              fontFamily="Inter, sans-serif"
            >
              {l.text}
            </text>
          ))}
          {/* The last six months, spaced evenly whatever the number of weeks in each */}
          {monthLabels.map((label, k) => (
            <text
              key={label + k}
              x={padL + (k / monthLabels.length) * plotW}
              y={H - 6}
              fontSize={fontAxis}
              fill={t.textTertiary}
              fontFamily="Inter, sans-serif"
            >
              {label}
            </text>
          ))}
          {drawn.map(
            (d) =>
              d.area && (
                <path key={`a-${d.s.key}`} d={d.area} fill={`url(#trend-fill-${d.s.key}-${compact ? 'c' : 'f'})`} />
              )
          )}
          {drawn.map(
            (d) =>
              d.line && (
                <path
                  key={`l-${d.s.key}`}
                  d={d.line}
                  fill="none"
                  stroke={TREND_COLORS[d.s.key]}
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              )
          )}
          {/* This week: a ringed dot and the value, in text ink */}
          {drawn.map(
            (d) =>
              d.last !== null &&
              d.last !== undefined && (
                <g key={`e-${d.s.key}`}>
                  <circle
                    cx={xAt(d.lastIdx)}
                    cy={yAt(d.last)}
                    r={4.5}
                    fill={TREND_COLORS[d.s.key]}
                    stroke={SURFACE}
                    strokeWidth={2}
                  />
                  <text
                    x={xAt(d.lastIdx) + 8}
                    y={labelY[d.s.key] ?? yAt(d.last) + 4}
                    fontSize={compact ? 12 : 12}
                    fontWeight={700}
                    fill={t.textPrimary}
                    fontFamily="Inter, sans-serif"
                  >
                    {formatValue(d.last)}
                  </text>
                </g>
              )
          )}
          {/* Hover crosshair */}
          {hover !== null && (
            <g pointerEvents="none">
              <line
                x1={xAt(hover)}
                x2={xAt(hover)}
                y1={padT}
                y2={padT + plotH}
                stroke={t.textSecondary}
                strokeWidth={1}
              />
              {series.map((s) =>
                s.points[hover] === null ? null : (
                  <circle
                    key={s.key}
                    cx={xAt(hover)}
                    cy={yAt(s.points[hover] as number)}
                    r={4}
                    fill={TREND_COLORS[s.key]}
                    stroke={SURFACE}
                    strokeWidth={2}
                  />
                )
              )}
            </g>
          )}
          <rect x={padL} y={padT} width={plotW} height={plotH} fill="transparent" />
          {/* Programs launched in the window, as dots on the line. After the
              hover rect, so they take the pointer. */}
          {eventDots.map(({ event, cx, cy }) => (
            <DosenTooltip
              key={`${event.kind}-${event.index}-${event.title}`}
              placement="bottom-start"
              componentsProps={{ tooltip: { sx: { maxWidth: 360 } } }}
              title={<EventTooltip event={event} />}
            >
              <circle
                cx={cx}
                cy={cy}
                r={hoverEvent === event ? 6 : 5}
                fill={EVENT_COLORS[event.kind]}
                stroke={SURFACE}
                strokeWidth={2}
                style={{ cursor: 'default' }}
                onMouseEnter={() => setHoverEvent(event)}
                onMouseLeave={() => setHoverEvent(null)}
              />
            </DosenTooltip>
          ))}
        </svg>
        {hover !== null && !hoverEvent && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: `${(xAt(hover) / W) * 100}%`,
              transform: xAt(hover) > W * 0.6 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)',
              bgcolor: '#151515',
              border: `1px solid ${GRID}`,
              borderRadius: '6px',
              px: '10px',
              py: '6px',
              pointerEvents: 'none',
              minWidth: 130
            }}
          >
            <Typography sx={{ fontSize: 11, color: t.textTertiary, mb: '2px' }}>{fmtWeek(win.weeks[hover])}</Typography>
            {series.map((s) => (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TREND_COLORS[s.key] }} />
                <Typography sx={{ fontSize: 12, color: t.textSecondary, flex: 1 }}>{s.label}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.textPrimary }}>
                  {s.points[hover] === null ? 'Not yet' : formatHover(s.points[hover] as number)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CapabilityTrend;
